// External libraries
import { noop } from 'lodash';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import styled from 'styled-components';

// Zocdoc libraries
import { grey25 } from '@zocdoc/branding/colors';
import { FontFamily } from '@zocdoc-frontend-common/styles-typography';
import { filterToValidAttributes } from '@zocdoc-frontend-common/utils-dom-property';
import { linkStyle } from '@zocdoc-frontend-common/components-link';
import Textbox from '@zocdoc-frontend-common/components-textbox';

class PhoneNumberWithoutFormatter extends PureComponent {
    static propTypes = {
        initialValue: PropTypes.string,
        invalidMessage: PropTypes.string,
        onChange: PropTypes.func,
        onEdit: PropTypes.func,
        presentationMode: PropTypes.bool,
        validationFunction: PropTypes.func,
        getFormatter: PropTypes.func,
    };

    static defaultProps = {
        onChange: noop,
        onEdit: noop,
        validationFunction: noop,
        getFormatter: noop,
    };

    static formatPhoneNumber(number, getFormatter) {
        const formatter = getFormatter();
        if (!formatter) {
            return number;
        }

        formatter.reset();

        return number.split('').reduce((str, digit) => {
            return formatter.addChar(digit);
        }, '');
    }

    static getDerivedStateFromProps(nextProps) {
        if (nextProps.initialValue) {
            return {
                formattedPhoneNumber: PhoneNumberWithoutFormatter.formatPhoneNumber(
                    nextProps.initialValue,
                    nextProps.getFormatter
                ),
            };
        }

        return null;
    }

    state = {
        formattedPhoneNumber: '',
        validationError: null,
    };

    render() {
        const {
            className,
            presentationMode,
            style,
            ...otherProps
        } = this.props;

        const attributes = filterToValidAttributes(otherProps);

        return (
            <Container className={className} style={style}>
                <StyledTextbox
                    {...attributes}
                    className="exclude-from-playback"
                    data-test="phone-number-input"
                    highlightText={this.props.highlightText}
                    invalidMessage={
                        this.props.invalidMessage || this.state.validationError
                    }
                    onBlur={this.handleBlur}
                    onChange={this.handleChange}
                    readOnly={presentationMode}
                    type="tel"
                    value={this.state.formattedPhoneNumber}
                >
                    {this.renderEditLink(presentationMode)}
                </StyledTextbox>
            </Container>
        );
    }

    renderEditLink(presentationMode) {
        if (presentationMode) {
            return (
                <EditButton
                    data-test="edit-phone-number-link"
                    onClick={this.props.onEdit}
                >
                    Edit
                </EditButton>
            );
        }
    }

    getOnlyNumbersAndPlusSign(input) {
        return input.replace(/[^0-9+]/g, '');
    }

    handleBlur = e => {
        const strippedNumbers = this.getOnlyNumbersAndPlusSign(e.target.value);
        const validationError = this.props.validationFunction(strippedNumbers);

        this.setState({ validationError });
    };

    handleChange = e => {
        const strippedNumbers = this.getOnlyNumbersAndPlusSign(e.target.value);
        const formattedPhoneNumber = PhoneNumberWithoutFormatter.formatPhoneNumber(
            strippedNumbers,
            this.props.getFormatter
        );

        this.setState({ formattedPhoneNumber });
        this.props.onChange(formattedPhoneNumber);
    };
}

const Container = styled.div`
    position: relative;
`;

const EditButton = styled.button`
    ${linkStyle};
    font-family: ${FontFamily.SharpSansSemibold};
    font-size: 15px;
    position: absolute;
    right: 20px;
    top: 16px;
`;

const StyledTextbox = styled(Textbox)`
    &:focus::placeholder {
        color: ${grey25};
    }
`;

export { PhoneNumberWithoutFormatter as default, PhoneNumberWithoutFormatter };
